import React from 'react';
import {
  MobileHeader, StatTiles, Card, SegmentedControl, FilterChip,
  BottomSheet, CollapsibleSection, EmptyState, ErrorState,
  Button, useToast,
} from '@parsina/framework';
import { LedgerSkeleton } from './Credits.skeleton.jsx';
import { C, CT } from '../../constants/theme.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { type } from '../../constants/typography.js';
import { creditLevel, creditColor, CREDIT_LEVEL_META } from '../../constants/credit.js';
import { fmtCoins, coinsToTomanLabel } from '../../constants/creditPricing.js';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { useCreditBalance, useCreditTransactions, useTopUp } from '../../hooks/useCredits.js';
import { CoinGlyph } from '../../components/common/CoinChip.jsx';
import { MobileSectionTitle } from '../../components/mobile/MobileKit.jsx';
import TopUpSheet from './TopUpSheet.jsx';
import GiftCodeCard from './GiftCodeCard.jsx';
import LowBalanceCard from './LowBalanceCard.jsx';
import {
  PAGE_SIZE, TYPE_FILTERS, GROUP_MODES, DATE_PRESETS, rangeToISO,
  getServiceMeta, parseTransactionLabels, groupByDay, groupByType, computeStats,
} from './Credits.shared.js';

// اعتبار — the ≤768px surface (DESIGN-CONCEPT-MOBILE).
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader (gradient) — PINNED    │
// │    └ the balance, inside the header   │  ۴۲۰ پارسه · «اعتبار خوب»
// │ ② StatTiles — 3-up, riding the edge   │  این ماه · هر جلسه · بیشترین
// ├──────────────────────────────────────┤  ── the scroll starts here ──
// │ ③ low/mid banner, when it applies     │
// │ ④ کد هدیه (compact)                    │
// │ ⑤ the ledger: chips + grouped rows    │
// │ ⑥ price list + هشدار, both collapsed   │
// │ [ BottomNav ]                          │
// └──────────────────────────────────────┘
//
// The BALANCE LIVES IN THE HEADER rather than in a hero card below it. §3.2's
// rule for a glanceable screen is that "the numbers never leave the screen", and
// on اعتبار the number that must never leave is the balance — the whole reason
// a dentist opens this page is to find out whether they can afford the next
// assistant call.
//
// The price list and the alert setting are COLLAPSED by default: they are
// reference material, and expanded they would push the ledger — the thing the
// dentist scrolled here to read — below two screens of static content.

const MobileTxnRow = ({ txn }) => {
  const { t, formatNumber, formatTime } = useTranslation();
  const meta = getServiceMeta(txn.serviceType, t);
  const Icon = meta.icon;
  const isDebit = txn.type === 'debit';
  const { title, sub } = parseTransactionLabels(txn.description, t);

  return (
    <div
      data-testid="credits-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        padding: '0.5625rem 0.75rem',
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <span style={{
        width: '1.875rem',
        height: '1.875rem',
        borderRadius: '0.5rem',
        background: meta.bg,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
      >
        <Icon size={15} color={meta.color} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...type.label, fontWeight: 700, color: C.textPrimary, display: 'block' }}>
          {title || meta.label}
        </span>
        <span style={{
          ...type.micro,
          color: C.textMuted,
          display: 'block',
          marginTop: '0.0625rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        >
          {formatTime(txn.createdAt)}
          {sub ? ` · ${sub}` : ''}
        </span>
      </span>
      <span style={{ ...type.body, fontWeight: 800, color: isDebit ? C.textSecondary : C.green }}>
        {isDebit ? '−' : '+'}
        {fmtCoins(txn.amount, formatNumber)}
      </span>
    </div>
  );
};

const MobileTypeGroupHeader = ({ group }) => {
  const { t, formatNumber } = useTranslation();
  const Icon = group.meta.icon;
  const spent = group.total >= 0;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.4375rem',
      padding: '0.375rem 0.75rem',
      background: C.bg,
    }}
    >
      <span style={{
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '0.375rem',
        background: group.meta.bg,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
      >
        <Icon size={12} color={group.meta.color} />
      </span>
      <span style={{ ...type.caption, fontWeight: 800, flex: 1, color: C.textPrimary }}>
        {group.meta.label}
      </span>
      <span style={{ ...type.micro, color: C.textMuted }}>
        {t('credits.timesUsed', { count: formatNumber(group.items.length) })}
      </span>
      <span style={{ ...type.label, fontWeight: 800, color: spent ? C.textSecondary : C.green }}>
        {spent ? '−' : '+'}
        {fmtCoins(Math.abs(group.total), formatNumber)}
      </span>
    </div>
  );
};

const CreditsMobile = () => {
  const { t, formatNumber, formatDate } = useTranslation();
  // The policy's figures once the list has arrived; the shipped constants until then.
  const { priceList, coinPriceToman } = useCreditPrices();
  const toast = useToast();

  const { balance, reload: reloadBalance } = useCreditBalance();
  const { startTopUp, busy: payBusy, error: payError } = useTopUp();

  const [page, setPage] = React.useState(0);
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [groupMode, setGroupMode] = React.useState('day');
  const [datePreset, setDatePreset] = React.useState('all');
  const [topUpOpen, setTopUpOpen] = React.useState(false);

  const { startDate, endDate } = React.useMemo(() => rangeToISO(datePreset), [datePreset]);

  const {
    transactions, totalCount, loading, error, reload,
  } = useCreditTransactions({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    startDate,
    endDate,
    type: typeFilter === 'all' ? '' : typeFilter,
  });

  // The gateway return leg. This surface needs its own copy and not a shared
  // one: a ≤768px viewport renders THIS component and never Credits.web, so
  // without it a dentist who paid on a phone lands back here with no
  // confirmation and a stale balance.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const settled = params.get('payment');
    if (!settled) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (settled === 'success') toast.success(t('credits.payment.done'));
    else toast.error(t('credits.payment.failed'));
    reloadBalance();
    reload();
    // Once per landing, never on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = React.useMemo(() => computeStats(transactions), [transactions]);
  const dayGroups = React.useMemo(
    () => groupByDay(transactions, t, formatDate),
    [transactions, t, formatDate],
  );
  const typeGroups = React.useMemo(() => groupByType(transactions, t), [transactions, t]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const level = creditLevel(balance);
  const topMeta = stats.topService ? getServiceMeta(stats.topService, t) : null;
  const filtered = typeFilter !== 'all' || datePreset !== 'all';

  // EXACTLY THREE TILES, always — and `columns={3}` below, which the kit
  // supports precisely "so a screen with three facts does not have to invent a
  // filler tile". Without it StatTiles lays out its default four columns and
  // the row renders with a hole in it.
  const tiles = [
    {
      key: 'month',
      label: t('credits.kpi.month'),
      value: fmtCoins(stats.usedThisMonth, formatNumber),
      icon: <CoinGlyph size={16} />,
      solid: true,
    },
    {
      key: 'perVisit',
      label: t('credits.kpi.perVisit'),
      value: stats.avgPerVisit == null ? '—' : fmtCoins(stats.avgPerVisit, formatNumber),
      icon: <CoinGlyph size={16} />,
      color: C.navy,
    },
    {
      key: 'top',
      label: topMeta ? topMeta.label : t('credits.kpi.topService'),
      value: topMeta ? t('credits.percent', { value: formatNumber(stats.topPct) }) : '—',
      icon: topMeta ? <topMeta.icon size={16} /> : <CoinGlyph size={16} />,
      color: topMeta ? topMeta.color : CT.mute,
    },
  ];

  return (
    <>
      {/* ── ① + ② the pinned zone. Outside the scroll region on purpose. ── */}
      <div style={{ flexShrink: 0 }}>
        <MobileHeader title={t('credits.title')} data-testid="credits-header">
          <div style={{
            background: 'rgba(255,255,255,0.12)',
            borderRadius: '0.875rem',
            padding: '0.75rem 0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...type.micro, color: 'rgba(255,255,255,0.6)' }}>
                {t('credits.currentBalance')}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.375rem', marginTop: '0.125rem' }}>
                <span style={{ ...type.display, color: '#fff' }} data-testid="credits-balance">
                  {balance === null ? '—' : fmtCoins(balance, formatNumber)}
                </span>
                <span style={{ ...type.unit, color: 'rgba(255,255,255,0.7)' }}>
                  {t('credits.unit')}
                </span>
                {level && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3125rem',
                    background: 'rgba(255,255,255,0.14)',
                    borderRadius: '1.25rem',
                    padding: '0.125rem 0.5rem',
                  }}
                  >
                    <span style={{
                      width: '0.4375rem',
                      height: '0.4375rem',
                      borderRadius: '50%',
                      background: creditColor(balance),
                    }}
                    />
                    <span style={{ ...type.micro, color: '#fff' }}>
                      {t(CREDIT_LEVEL_META[level].shortLabelKey)}
                    </span>
                  </span>
                )}
              </div>
              {balance !== null && (
                <div style={{ ...type.micro, color: 'rgba(255,255,255,0.55)', marginTop: '0.25rem' }}>
                  {coinsToTomanLabel(balance, { formatNumber, tomanLabel: t('credits.toman'), tomanPerCoin: coinPriceToman })}
                </div>
              )}
            </div>
            <Button
              onClick={() => setTopUpOpen(true)}
              data-testid="credits-topup"
              className="min-h-11 shrink-0"
              style={{ background: '#fff', color: C.navy }}
            >
              <CoinGlyph size={14} />
              <span style={{ marginInlineStart: '0.375rem' }}>{t('credits.buy')}</span>
            </Button>
          </div>
        </MobileHeader>

        <div style={{ padding: '0 0.75rem', marginTop: '-1rem', position: 'relative' }}>
          <StatTiles
            tiles={tiles}
            columns={3}
            loading={loading && !transactions.length}
            data-testid="credits-stats"
          />
        </div>
      </div>

      {/* ── the ONE page scroll ── */}
      <div
        data-testid="credits-content"
        data-state={error ? 'error' : loading ? 'loading' : transactions.length ? 'content' : 'empty'}
        aria-busy={loading || undefined}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Clear of the bottom bar, so the last row is never covered.
          padding: '0.875rem 0.75rem 1.5rem',
        }}
      >
        {/* ③ Only when it actually applies. A banner that is always there is
            not a banner. */}
        {(level === 'low' || level === 'mid') && (
          <Card
            data-testid="credits-low-banner"
            style={{
              background: CREDIT_LEVEL_META[level].tint,
              borderColor: CREDIT_LEVEL_META[level].color,
              marginBottom: '0.875rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ ...type.label, color: CREDIT_LEVEL_META[level].color, flex: 1, lineHeight: 1.8 }}>
                {t(level === 'low' ? 'credits.banner.low' : 'credits.banner.mid', {
                  coins: fmtCoins(balance, formatNumber),
                })}
              </span>
              <Button
                variant="outline"
                onClick={() => setTopUpOpen(true)}
                className="min-h-11 shrink-0"
              >
                {t('credits.topUp')}
              </Button>
            </div>
          </Card>
        )}

        {/* ④ Above everything else on purpose: a dentist who arrived from a
            campaign SMS has a code in hand and nothing else to do here.
            `compact` drops the two explanatory lines — on a phone they push the
            field itself below the fold. */}
        <GiftCodeCard compact onRedeemed={() => { reloadBalance(); reload(); }} />

        {/* ⑤ The ledger */}
        <section style={{ marginTop: '0.875rem' }}>
          <MobileSectionTitle count={totalCount ? formatNumber(totalCount) : undefined}>
            {t('credits.transactions')}
          </MobileSectionTitle>

          <div
            role="group"
            aria-label={t('credits.filter.label')}
            style={{ display: 'flex', gap: '0.375rem', overflowX: 'auto', paddingBottom: '0.25rem' }}
          >
            {TYPE_FILTERS(t).map((f) => (
              <FilterChip
                key={f.value}
                label={f.label}
                active={typeFilter === f.value}
                onClick={() => { setTypeFilter(f.value); setPage(0); }}
              />
            ))}
          </div>

          <div
            role="group"
            aria-label={t('credits.range.label')}
            style={{ display: 'flex', gap: '0.375rem', overflowX: 'auto', padding: '0.375rem 0 0.5rem' }}
            data-testid="credits-daterange"
          >
            {DATE_PRESETS(t).map((p) => (
              <FilterChip
                key={p.value}
                label={p.label}
                active={datePreset === p.value}
                onClick={() => { setDatePreset(p.value); setPage(0); }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ ...type.micro, color: C.textMuted }}>{t('credits.group.label')}</span>
            <SegmentedControl
              options={GROUP_MODES(t)}
              value={groupMode}
              onChange={setGroupMode}
              size="lg"
            />
          </div>

          {error && !loading && transactions.length === 0 && (
            <div role="alert">
              <ErrorState
                title={t('credits.error.title')}
                description={t('credits.error.body')}
                onRetry={reload}
                retryLabel={t('common.actions.retry')}
              />
            </div>
          )}

          {/* Only the ledger body skeletons, in MobileTxnRow's own shape and
              inside the same Card the rows land in; the pinned header, the
              tiles and the filters stay real (DESIGN.md §"Loading &
              skeleton", region load). */}
          {loading && transactions.length === 0 && (
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <LedgerSkeleton mobile label={t('common.loading')} />
            </Card>
          )}

          {!loading && !error && transactions.length === 0 && (
            <Card>
              <EmptyState
                icon={<span style={{ color: C.textMuted, display: 'inline-flex' }}><CoinGlyph size={44} /></span>}
                title={filtered ? t('credits.empty.filtered') : t('credits.empty.title')}
                description={filtered ? t('credits.empty.filteredHint') : t('credits.empty.hint')}
              />
            </Card>
          )}

          {!loading && !error && transactions.length > 0 && (
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              {groupMode === 'day' && dayGroups.map((group) => (
                <div key={group.key}>
                  <div style={{
                    ...type.micro,
                    fontWeight: 700,
                    padding: '0.3125rem 0.75rem',
                    color: C.textMuted,
                    background: C.bg,
                  }}
                  >
                    {group.label}
                  </div>
                  {group.items.map((txn) => <MobileTxnRow key={txn.id} txn={txn} />)}
                </div>
              ))}
              {groupMode === 'type' && typeGroups.map((group) => (
                <div key={group.key}>
                  <MobileTypeGroupHeader group={group} />
                  {group.items.map((txn) => <MobileTxnRow key={txn.id} txn={txn} />)}
                </div>
              ))}
            </Card>
          )}

          {/* A PAGER, and deliberately not the «نمایش بیشتر» expander the other
              mobile lists use. Those APPEND; this hook REPLACES its page, so a
              grow button would silently discard the rows already on screen —
              and a ledger is the one list where stepping BACK matters. */}
          {totalPages > 1 && !loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              marginTop: '0.625rem',
            }}
            >
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="min-h-11"
                data-testid="credits-prev"
              >
                {t('credits.pager.previous')}
              </Button>
              <span style={{ ...type.label, color: C.textSecondary, whiteSpace: 'nowrap' }} data-testid="credits-page-label">
                {t('credits.pager.page', {
                  page: formatNumber(page + 1),
                  total: formatNumber(totalPages),
                })}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                disabled={page + 1 >= totalPages}
                className="min-h-11"
                data-testid="credits-next"
              >
                {t('credits.pager.next')}
              </Button>
            </div>
          )}
        </section>

        {/* ⑥ Reference material, collapsed. */}
        <section style={{ marginTop: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          <CollapsibleSection title={t('credits.priceList.title')} defaultOpen={false}>
            <div style={{ ...type.caption, color: C.textMuted, lineHeight: 1.9, marginBottom: '0.5rem' }}>
              {t('credits.priceList.hint')}
            </div>
            {priceList.map((row, i) => (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.3125rem 0',
                  borderTop: i ? `1px solid ${C.border}` : 'none',
                }}
              >
                <span style={{ ...type.caption, flex: 1, color: C.textSecondary }}>
                  {t(row.labelKey)}
                </span>
                <span style={{
                  ...type.caption,
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: C.teal,
                }}
                >
                  <CoinGlyph size={10} />
                  {fmtCoins(row.cost, formatNumber)}
                  {row.perKey ? ` / ${t(row.perKey)}` : ''}
                </span>
              </div>
            ))}
          </CollapsibleSection>

          <LowBalanceCard />
        </section>
      </div>

      <BottomSheet
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        title={t('credits.buy')}
        closeLabel={t('mobile.close')}
      >
        <TopUpSheet
          avgPerVisit={stats.avgPerVisit}
          onPay={startTopUp}
          busy={payBusy}
          error={payError}
        />
      </BottomSheet>
    </>
  );
};

export default CreditsMobile;
